// src/services/api/timeEntries.service.ts

import { apiClient, makeApiRequest } from './client';
import { endpoints } from './config';
import { 
  TimeEntry, 
  CreateTimeEntryRequest, 
  UpdateTimeEntryRequest,
  TimeEntryFilters
} from './types';

export class TimeEntriesService {
  async getTimeEntries(filters?: TimeEntryFilters): Promise<TimeEntry[]> {
    return makeApiRequest(() => 
      apiClient.get<TimeEntry[]>(endpoints.timeEntries.list, filters)
    );
  }

  async createTimeEntry(entryData: CreateTimeEntryRequest): Promise<TimeEntry> {
    return makeApiRequest(() => 
      apiClient.post<TimeEntry>(endpoints.timeEntries.create, entryData)
    );
  }

  async updateTimeEntry(id: number, updates: UpdateTimeEntryRequest): Promise<TimeEntry> {
    return makeApiRequest(() => 
      apiClient.put<TimeEntry>(endpoints.timeEntries.update(id), updates)
    );
  }

  async deleteTimeEntry(id: number): Promise<void> {
    return makeApiRequest(() => 
      apiClient.delete(endpoints.timeEntries.delete(id))
    );
  }

  async getTimeEntriesByUser(
    userId: number, 
    startDate?: string, 
    endDate?: string
  ): Promise<TimeEntry[]> {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    return makeApiRequest(() => 
      apiClient.get<TimeEntry[]>(endpoints.timeEntries.byUser, {
        user_id: userId,
        ...params
      })
    );
  }

  async getTimeEntriesForTask(taskId: number): Promise<TimeEntry[]> {
    return makeApiRequest(() => 
      apiClient.get<TimeEntry[]>(endpoints.tasks.timeEntries(taskId))
    );
  }

  // Helper method for logging hours (handles create or update logic)
  async logHours(
    taskId: number,
    projectId: number,
    date: string,
    hours: number,
    description?: string,
    employeeId?: number
  ): Promise<TimeEntry> {
    const entryData: CreateTimeEntryRequest = {
      task_id: taskId,
      project_id: projectId,
      date,
      hours,
      description,
      employee_id: employeeId,
    };

    return this.createTimeEntry(entryData);
  }

  // Get weekly time entries for PM dashboard
  async getWeeklyTimeEntries(
    startDate: string,
    endDate: string,
    projectId?: number
  ): Promise<TimeEntry[]> {
    const filters: TimeEntryFilters = {
      start_date: startDate,
      end_date: endDate,
    };

    if (projectId) {
      filters.project_id = projectId;
    }

    return this.getTimeEntries(filters);
  }

  // Get daily time entries for a specific user and date
  async getDailyTimeEntries(
    userId: number,
    date: string
  ): Promise<TimeEntry[]> {
    const entries = await this.getTimeEntriesByUser(userId, date, date);
    return Array.isArray(entries) ? entries : [];
  }

  // Calculate total hours for a user in a date range
  async getUserTotalHours(
    userId: number,
    startDate: string,
    endDate: string
  ): Promise<number> {
    const entries = await this.getTimeEntriesByUser(userId, startDate, endDate);
    return entries.reduce((total, entry) => total + entry.hours, 0);
  }

  // Get time summary for a project
  async getProjectTimeSummary(
    projectId: number,
    startDate?: string,
    endDate?: string
  ): Promise<{
    totalHours: number;
    employeeHours: { [employeeId: number]: number };
    taskHours: { [taskId: number]: number };
  }> {
    const filters: TimeEntryFilters = { project_id: projectId };
    if (startDate) filters.start_date = startDate;
    if (endDate) filters.end_date = endDate;

    const entries = await this.getTimeEntries(filters);

    const summary = {
      totalHours: 0,
      employeeHours: {} as { [employeeId: number]: number },
      taskHours: {} as { [taskId: number]: number },
    };

    entries.forEach(entry => {
      summary.totalHours += entry.hours;
      summary.employeeHours[entry.employee_id] = 
        (summary.employeeHours[entry.employee_id] || 0) + entry.hours;
      summary.taskHours[entry.task_id] = 
        (summary.taskHours[entry.task_id] || 0) + entry.hours;
    });

    return summary;
  }
}

export const timeEntriesService = new TimeEntriesService();
